import React from 'react'
import {modusText} from "./LiveTVScoreboard";
// Schrift selbst gehostet (kein Abruf bei Google), nur die genutzten Stärken
import "@fontsource/plus-jakarta-sans/600.css";
import "@fontsource/plus-jakarta-sans/700.css";
import "@fontsource/plus-jakarta-sans/800.css";
import "./liveListe.css";

export const FILTER = [
    {id: "alle", label: "Alle"},
    {id: "live", label: "Läuft"},
    {id: "startet", label: "Startet"},
    {id: "beendet", label: "Beendet"},
];

/**
 * Status eines Spiels für Anzeige und Filter: live, check (ein Spieler ist im Check), startet, imspiel, beendet.
 */
export function spielStatus(game, checkedPlayerIds) {
    if (game.abgeschlossen) {
        return "beendet";
    }
    if (game.startssoon) {
        return "startet";
    }
    if (game.inGame) {
        return "imspiel";
    }
    if (checkedPlayerIds.includes(game.spieler1Id) || checkedPlayerIds.includes(game.spieler2Id)) {
        return "check";
    }
    return "live";
}

// für den Filter gehören "im Spiel" zu "Startet" und "Check" zu "Läuft"
function filterGruppe(status) {
    if (status === "imspiel") {
        return "startet";
    }
    if (status === "check") {
        return "live";
    }
    return status;
}

export function filtereSpiele(games, filter, checkedPlayerIds) {
    if (filter === "alle") {
        return games;
    }
    return games.filter(game => filterGruppe(spielStatus(game, checkedPlayerIds)) === filter);
}

/**
 * Filter über der Live-Liste (Alle, Läuft, Startet, Beendet) mit der Anzahl der Spiele.
 */
export function LiveListeFilter({games, checkedPlayerIds, filter, onFilter}) {
    const anzahl = (id) => filtereSpiele(games, id, checkedPlayerIds).length;
    return <div className="lvl-filter" role="radiogroup" aria-label="Spiele filtern">
        {FILTER.map(f => <button key={f.id} type="button" role="radio" aria-checked={filter === f.id}
                                 className="lvl-filter-knopf" onClick={() => onFilter(f.id)}>
            {f.label} {anzahl(f.id)}
        </button>)}
    </div>
}

const STATUS_TEXT = {live: "Live", check: "Check", startet: "Startet", imspiel: "Im Spiel", beendet: "Beendet"};

/**
 * Live-Spiele als schlichte Liste: je Spiel eine Zeile mit Board, beiden Spielern, Restpunkten, Stand und Status.
 * Ist der Platz schmal (z.B. Handy), wird jede Zeile zur Karte (Container-Query in liveListe.css).
 */
export default function LiveListe({games, checkedPlayerIds, onDetail}) {
    if (games.length === 0) {
        return <div className="lvl"><div className="lvl-leer">Keine Spiele für diesen Filter.</div></div>
    }
    return <div className="lvl">
        <div className="lvl-kopf" aria-hidden="true">
            <span>Board</span>
            <span className="lvl-rechts">Spieler</span>
            <span className="lvl-mitte">Rest</span>
            <span className="lvl-mitte">Legs</span>
            <span className="lvl-mitte">Rest</span>
            <span>Spieler</span>
            <span className="lvl-rechts">Status</span>
        </div>
        {games.map(game => <Zeile key={game.gameId} game={game} checkedPlayerIds={checkedPlayerIds} onDetail={onDetail}/>)}
    </div>
}

function Zeile({game, checkedPlayerIds, onDetail}) {
    const status = spielStatus(game, checkedPlayerIds);
    const abgeschlossen = !!game.abgeschlossen;
    const istSetModus = game.sets > 1;
    const istEDart = game.dartType === "EDART";

    const spieler = [1, 2].map(nr => {
        const id = game["spieler" + nr + "Id"];
        const legs = game["spieler" + nr + "Legs"];
        const sets = game["spieler" + nr + "Sets"];
        const gegnerLegs = game["spieler" + (3 - nr) + "Legs"];
        const gegnerSets = game["spieler" + (3 - nr) + "Sets"];
        const sieger = abgeschlossen && (istSetModus ? sets > gegnerSets : legs > gegnerLegs);
        return {
            nr, legs, sets, sieger,
            name: game["spieler" + nr + "Name"],
            rest: istEDart ? "-" : game["spieler" + nr + "Togo"],
            aktiv: !abgeschlossen && id === game.spielerIdActive,
            anwurf: !abgeschlossen && id === game.spielerIdLegAnwurf,
            verloren: abgeschlossen && !sieger,
        };
    });

    const oeffnen = () => onDetail(game.boardId);

    return <div className="lvl-zeile" role="button" tabIndex={0} onClick={oeffnen}
                onKeyDown={(event) => (event.key === "Enter" || event.key === " ") && oeffnen()}
                aria-label={(game.boardName || "") + ": " + spieler[0].name + " gegen " + spieler[1].name + ", Details anzeigen"}>
        <div className="lvl-board">
            <span className="lvl-board-nr">{boardKurz(game.boardName)}</span>
            <span className="lvl-board-text">
                <span className="lvl-board-name">{game.boardName}<span className="lvl-modus-schmal"> · {modusText(game)}</span></span>
                <span className="lvl-schreiber">{vorhanden(game.schreiberName) ? "Schreiber: " + game.schreiberName : "ohne Schreiber"}</span>
            </span>
        </div>
        {spieler.map(s => <React.Fragment key={s.nr}>
            <div className={"lvl-name lvl-name-" + s.nr}>
                {s.anwurf && <span className="lvl-anwurf" title="Anwurf im Leg" aria-label="Anwurf im Leg"/>}
                <span className={"lvl-name-text" + (s.aktiv || s.sieger ? " lvl-name-stark" : "") + (s.verloren ? " lvl-name-verloren" : "")}>{s.name}</span>
                {s.sieger && <span className="lvl-sieger">Sieger</span>}
            </div>
            <span className={"lvl-rest lvl-rest-" + s.nr + (s.aktiv ? " lvl-rest-aktiv" : "")}>{s.rest}</span>
            <span className={"lvl-legs-schmal lvl-legs-schmal-" + s.nr}>{istSetModus ? s.sets + " / " + s.legs : s.legs}</span>
        </React.Fragment>)}
        <div className="lvl-stand">
            {istSetModus && <span className="lvl-stand-sets">Sets {spieler[0].sets} : {spieler[1].sets}</span>}
            <span className="lvl-stand-legs">{spieler[0].legs} : {spieler[1].legs}</span>
            <span className="lvl-stand-modus">{modusText(game)}</span>
        </div>
        <div className="lvl-status">
            <span className={"lvl-status-schild lvl-status-" + status}>{STATUS_TEXT[status]}</span>
        </div>
    </div>
}

// kurze Kennung fürs Kästchen: die Nummer am Ende ("Board 3" -> "3"), sonst die ersten zwei Zeichen
function boardKurz(boardName) {
    if (!vorhanden(boardName)) {
        return "–";
    }
    const nummer = String(boardName).match(/(\d+)\s*$/);
    return nummer ? nummer[1] : String(boardName).trim().substring(0, 2).toUpperCase();
}

function vorhanden(wert) {
    return wert !== null && wert !== undefined && wert !== "";
}
